/* ----------------------------------------------------------------------------
 * Summer landing page behaviours (header drawer, feature-cards carousel,
 * FAQ accordion). Loaded by the landing-* sections; guarded so it can be
 * included by several sections at once.
 * -------------------------------------------------------------------------- */

(function () {
  'use strict';

  if (window.__mslLandingInit) {
    return;
  }
  window.__mslLandingInit = true;

  /* --------------------------------------------------------------
   * Header: sticky background + mobile drawer
   * -------------------------------------------------------------- */
  if (!customElements.get('msl-header')) {
    customElements.define(
      'msl-header',
      class extends HTMLElement {
        connectedCallback() {
          this.burger = this.querySelector('.msl-header__burger');

          if (this.burger) {
            this.burger.addEventListener('click', () => this.toggle());
          }

          this.querySelectorAll('.msl-header__drawer a').forEach((link) => {
            link.addEventListener('click', () => this.toggle(false));
          });

          this._onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 30);
          window.addEventListener('scroll', this._onScroll, { passive: true });
          this._onScroll();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this._onScroll);
        }

        toggle(force) {
          const open = force !== undefined ? force : !this.classList.contains('is-open');
          this.classList.toggle('is-open', open);

          if (this.burger) {
            this.burger.setAttribute('aria-expanded', open ? 'true' : 'false');
          }
        }
      }
    );
  }

  /* --------------------------------------------------------------
   * Feature cards carousel (scroll-snap based, works in RTL)
   * -------------------------------------------------------------- */
  if (!customElements.get('msl-carousel')) {
    customElements.define(
      'msl-carousel',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.msl-cards__track');
          this.slides = Array.from(this.querySelectorAll('.msl-card'));
          this.dots = Array.from(this.querySelectorAll('.msl-cards__dot'));
          this.activeIndex = 0;

          if (!this.track || this.slides.length === 0) {
            return;
          }

          // Autoplay config (from section settings via data-attributes)
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const designMode = window.Shopify && window.Shopify.designMode;
          this.autoplayEnabled =
            this.dataset.autoplay !== 'false' && this.slides.length > 1 && !reducedMotion && !designMode;
          this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
          this._paused = false;

          this.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
              this.goTo(index);
              this.restartAutoplay();
            });
          });

          this.slides.forEach((slide, index) => {
            slide.addEventListener('click', () => {
              if (!slide.classList.contains('is-active')) {
                this.goTo(index);
              }
              this.restartAutoplay();
            });
          });

          this._onScroll = () => {
            window.cancelAnimationFrame(this._raf);
            this._raf = window.requestAnimationFrame(() => this.update());
          };
          this.track.addEventListener('scroll', this._onScroll, { passive: true });
          window.addEventListener('resize', this._onScroll);

          // Start centered on the slide marked as initial (default: first)
          const initial = Math.max(this.slides.findIndex((slide) => slide.hasAttribute('data-initial')), 0);
          this.goTo(initial, 'instant');
          this.update();

          this.setupAutoplay();
        }

        disconnectedCallback() {
          window.removeEventListener('resize', this._onScroll);
          this.stopAutoplay();

          if (this._observer) {
            this._observer.disconnect();
          }

          document.removeEventListener('visibilitychange', this._onVisibility);
        }

        setupAutoplay() {
          if (!this.autoplayEnabled) {
            return;
          }

          // Pause on user interaction, resume when it ends
          this.addEventListener('pointerenter', () => this.pause(true));
          this.addEventListener('pointerleave', () => this.pause(false));
          this.addEventListener('focusin', () => this.pause(true));
          this.addEventListener('focusout', () => this.pause(false));
          this.addEventListener('touchstart', () => this.pause(true), { passive: true });
          this.addEventListener('touchend', () => this.pause(false), { passive: true });

          // Pause while the tab is hidden
          this._onVisibility = () => {
            if (document.hidden) {
              this.stopAutoplay();
            } else if (!this._paused && this._visible) {
              this.startAutoplay();
            }
          };
          document.addEventListener('visibilitychange', this._onVisibility);

          // Only run while the carousel is actually on screen
          this._visible = true;

          if ('IntersectionObserver' in window) {
            this._observer = new IntersectionObserver(
              (entries) => {
                this._visible = entries[0].isIntersecting;

                if (this._visible && !this._paused && !document.hidden) {
                  this.startAutoplay();
                } else {
                  this.stopAutoplay();
                }
              },
              { threshold: 0.3 }
            );
            this._observer.observe(this);
          } else {
            this.startAutoplay();
          }
        }

        startAutoplay() {
          if (!this.autoplayEnabled || this._timer) {
            return;
          }

          this._timer = window.setInterval(() => {
            const next = (this.activeIndex + 1) % this.slides.length;
            this.goTo(next);
          }, this.autoplaySpeed);
        }

        stopAutoplay() {
          if (this._timer) {
            window.clearInterval(this._timer);
            this._timer = null;
          }
        }

        pause(state) {
          this._paused = state;

          if (state) {
            this.stopAutoplay();
          } else if (this._visible && !document.hidden) {
            this.startAutoplay();
          }
        }

        // Restart the countdown so a manual pick doesn't immediately advance
        restartAutoplay() {
          if (!this.autoplayEnabled || this._paused) {
            return;
          }

          this.stopAutoplay();
          this.startAutoplay();
        }

        goTo(index, behavior) {
          const slide = this.slides[index];

          if (slide && this.track) {
            // Scroll only the track horizontally (not slide.scrollIntoView, which can
            // also drag the whole page's vertical scroll to reveal the section on load).
            const offset = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
            this.track.scrollTo({ left: offset, behavior: behavior || 'smooth' });
          }
        }

        update() {
          const center = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
          let activeIndex = 0;
          let minDistance = Infinity;

          this.slides.forEach((slide, index) => {
            const rect = slide.getBoundingClientRect();
            const distance = Math.abs(rect.left + rect.width / 2 - center);

            if (distance < minDistance) {
              minDistance = distance;
              activeIndex = index;
            }
          });

          this.activeIndex = activeIndex;

          this.slides.forEach((slide, index) => {
            const rect = slide.getBoundingClientRect();
            const slideCenter = rect.left + rect.width / 2;
            slide.classList.toggle('is-active', index === activeIndex);
            slide.classList.toggle('is-prev', index !== activeIndex && slideCenter < center);
            slide.classList.toggle('is-next', index !== activeIndex && slideCenter > center);
          });

          this.dots.forEach((dot, index) => {
            dot.classList.toggle('is-active', index === activeIndex);
            dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
          });
        }
      }
    );
  }

  /* --------------------------------------------------------------
   * Movies marquee (auto-scroll strip, draggable with mouse/touch)
   * -------------------------------------------------------------- */
  if (!customElements.get('msl-marquee-viewport')) {
    customElements.define(
      'msl-marquee-viewport',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.msl-marquee__track');

          if (!this.track) {
            return;
          }

          const duration = parseFloat(getComputedStyle(this).getPropertyValue('--msl-marquee-duration'));
          this.duration = duration > 0 ? duration : 40;
          this.offset = 0;
          this.isDragging = false;
          this._visible = true;
          this._lastTime = null;

          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const designMode = window.Shopify && window.Shopify.designMode;
          this.autoplayEnabled = !reducedMotion && !designMode;

          // Hand control over to JS; if this script fails to init for any reason,
          // the CSS keyframe animation (still defined in the stylesheet) is the fallback.
          this.track.style.animation = 'none';
          this.track.style.willChange = 'transform';

          this.measure();

          this._onResize = () => this.measure();
          window.addEventListener('resize', this._onResize);

          this._tick = (time) => this.tick(time);

          this._onVisibility = () => {
            if (document.hidden) {
              this.stop();
            } else if (this._visible && !this.isDragging) {
              this.start();
            }
          };
          document.addEventListener('visibilitychange', this._onVisibility);

          if ('IntersectionObserver' in window) {
            this._observer = new IntersectionObserver(
              (entries) => {
                this._visible = entries[0].isIntersecting;

                if (this._visible && !document.hidden && !this.isDragging) {
                  this.start();
                } else {
                  this.stop();
                }
              },
              { threshold: 0.1 }
            );
            this._observer.observe(this);
          } else {
            this.start();
          }

          this.setupDrag();
          this.setupHoverPause();
        }

        disconnectedCallback() {
          this.stop();
          window.removeEventListener('resize', this._onResize);
          document.removeEventListener('visibilitychange', this._onVisibility);

          if (this._observer) {
            this._observer.disconnect();
          }
        }

        measure() {
          // The track holds two identical copies of the items back to back so the
          // loop can be seamless. `scrollWidth / 2` looks like the right distance to
          // shift before wrapping, but it's off by half a gap (flex `gap` isn't split
          // evenly across "two halves" the way it splits across items), so the loop
          // restarted early and left a visible blank gap after the last real image.
          // Measuring the actual offset of the first item in the second copy avoids
          // that rounding error regardless of item width or gap size.
          const items = this.track.children;
          const half = Math.floor(items.length / 2);
          this.halfWidth =
            half > 0 && items[half] ? items[half].offsetLeft - items[0].offsetLeft : this.track.scrollWidth / 2 || 0;
          this.speed = this.halfWidth / (this.duration * 1000);
          this.applyOffset();
        }

        start() {
          if (!this.autoplayEnabled || this.isDragging || this._raf || !this.halfWidth) {
            return;
          }

          this._lastTime = null;
          this._raf = window.requestAnimationFrame(this._tick);
        }

        stop() {
          if (this._raf) {
            window.cancelAnimationFrame(this._raf);
            this._raf = null;
          }
        }

        tick(time) {
          if (this._lastTime !== null) {
            const delta = time - this._lastTime;
            this.offset -= this.speed * delta;
            this.wrap();
            this.applyOffset();
          }

          this._lastTime = time;
          this._raf = window.requestAnimationFrame(this._tick);
        }

        // Keep the offset inside a single copy's width so the duplicated
        // track loops seamlessly in either direction.
        wrap() {
          if (!this.halfWidth) {
            return;
          }

          while (this.offset <= -this.halfWidth) {
            this.offset += this.halfWidth;
          }

          while (this.offset > 0) {
            this.offset -= this.halfWidth;
          }
        }

        applyOffset() {
          this.track.style.transform = `translateX(${this.offset}px)`;
        }

        setupHoverPause() {
          this.addEventListener('pointerenter', () => {
            if (!this.isDragging) {
              this.stop();
            }
          });

          this.addEventListener('pointerleave', () => {
            if (!this.isDragging) {
              this.start();
            }
          });
        }

        setupDrag() {
          let startX = 0;
          let startOffset = 0;

          const onPointerDown = (event) => {
            if (event.button !== undefined && event.button !== 0 && event.pointerType === 'mouse') {
              return;
            }

            this.isDragging = true;
            startX = event.clientX;
            startOffset = this.offset;
            this.stop();
            this.classList.add('is-dragging');

            if (this.setPointerCapture) {
              try {
                this.setPointerCapture(event.pointerId);
              } catch (error) {
                /* Pointer capture is best-effort; ignore unsupported targets. */
              }
            }
          };

          const onPointerMove = (event) => {
            if (!this.isDragging) {
              return;
            }

            const delta = event.clientX - startX;
            this.offset = startOffset + delta;
            this.wrap();
            this.applyOffset();
          };

          const endDrag = () => {
            if (!this.isDragging) {
              return;
            }

            this.isDragging = false;
            this.classList.remove('is-dragging');
            this.start();
          };

          this.addEventListener('pointerdown', onPointerDown);
          this.addEventListener('pointermove', onPointerMove);
          this.addEventListener('pointerup', endDrag);
          this.addEventListener('pointercancel', endDrag);
          this.addEventListener('pointerleave', endDrag);
        }
      }
    );
  }

  /* --------------------------------------------------------------
   * FAQ accordion (one item open at a time)
   * -------------------------------------------------------------- */
  if (!customElements.get('msl-accordion')) {
    customElements.define(
      'msl-accordion',
      class extends HTMLElement {
        connectedCallback() {
          this.items = Array.from(this.querySelectorAll('.msl-faq-item'));

          this.items.forEach((item) => {
            const toggle = item.querySelector('.msl-faq-item__toggle');

            if (toggle) {
              toggle.addEventListener('click', () => this.toggleItem(item));
            }
          });
        }

        toggleItem(item) {
          const willOpen = !item.classList.contains('is-open');

          this.items.forEach((other) => this.setOpen(other, false));

          if (willOpen) {
            this.setOpen(item, true);
          }
        }

        setOpen(item, open) {
          item.classList.toggle('is-open', open);
          const toggle = item.querySelector('.msl-faq-item__toggle');

          if (toggle) {
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          }
        }
      }
    );
  }
})();